/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { ToggleModalProps } from '../../../../components/modal/types';
import type { OptionType } from '../../../../util/types';
import type { NewClassGetYearGroupsItem } from '@whizz/api-model/src/endpoints/NewClass';
import type { NewClassGetItem } from '@whizz/api-model';
import React from 'react';
import { useI18n } from '@whizz/react-i18n';
import { Button, ButtonIcon, Selector, Textarea } from '../../../../components';
import { CancelButtonModal, FormGenericError, SaveButton } from '../../../../global';
import { GLOBAL_ICONS } from '../../../../util/vars';
import { useQueryClient } from 'react-query';
import { FieldArray, Formik } from 'formik';
import * as Yup from 'yup';
import { ModalFooter } from '../../../../components/modal/Modal';

const APPLIES = [
  { label: 'School', value: 'school' },
  { label: 'Class', value: 'class' },
  { label: 'Year Group', value: 'yearGroup' },
];

const AddEditGoalModal = ({ toggle, toggleConfirm, data, defaultGoals, yearGroups, schoolClasses, onSuccess, onError }: GoalModalProps): JSX.Element => {
  const { translate: t } = useI18n();
  const queryClient = useQueryClient();

  const editMode = null != data;

  const handleDelete = () => {
    toggle(false);
    toggleConfirm(true);
  };

  const VALIDATION_SCHEMA = Yup.object().shape({
    name: Yup.string().required(t('pages.ROSTER_MANAGEMENT.CLASSES.ADD_EDIT.VALIDATIONS.NAME.REQUIRED')),
    yearGroupId: Yup.number().nullable(),
    color: Yup.number().nullable(),
  });

  const GOALS = defaultGoals?.map((goal) => ({
    label: t(`pages.ADMIN.IMPACT_REPORTS.GOALS.${goal.goal_key}`),
    value: goal.goal_key,
  })) || [];

  return (
    <Formik<GoalProps>
      initialValues={{
        goal_key: '',
        cohort: {
          id: null,
          name: '',
        },
        metrics: [],
        notes: '',
        ...(editMode ? data : {}),
      }}
      onSubmit={(formData) => {
        onSuccess?.(formData);
        toggle(false);
      }}
    >
      {({ handleSubmit, values, setFieldValue, errors, resetForm, isValid, touched }) => {

        const METRICS_COMPONENTS = defaultGoals?.find((goal) => goal.goal_key === values.goal_key)?.metrics || [];

        let cohortOptions: OptionType<number>[] | null = null;

        switch (values.cohort.name) {
          case 'yearGroup':
            cohortOptions = yearGroups.map((yearGroup) => ({
              label: yearGroup.name,
              value: yearGroup.id,
            }));
            break;
          case 'class':
            cohortOptions = schoolClasses.map((classItem) => ({
              label: classItem.name,
              value: classItem.id,
            }));
            break;
          case 'school':
          default:
            cohortOptions = null;
            //setFieldValue('cohort.id', null);
            break;
        }

        return (
          <form onSubmit={handleSubmit}>
            <div className={'container space-y-6'}>
              <div className={'space-y-2'}>
                <h3 className={'font-bold'}>Goal</h3>
                <Selector
                  id={'goal_key'}
                  label={''}
                  hideLabel
                  isClearable={false}
                  labelStyle={'bold'}
                  options={GOALS}
                  onChange={(option: OptionType<string>) => setFieldValue('goal_key', option.value)}
                  value={GOALS.find((option) => option.value === values.goal_key) || null}
                  error={errors.goal_key}
                />
              </div>
              <div className={'space-y-2'}>
                <h3 className={'font-bold'}>Applies to</h3>
                <div className={'flex space-x-4'}>
                  <div className={'flex-grow'}>
                    <Selector
                      id={'cohort.name'}
                      label={''}
                      hideLabel
                      isClearable={false}
                      options={APPLIES}
                      onChange={(option: OptionType<string>) => {
                        setFieldValue('cohort.name', option.value);
                        setFieldValue('cohort.id', null);
                      }}
                      value={APPLIES.find((option) => option.value === values.cohort.name) || null}
                      error={errors.cohort?.name}
                    />
                  </div>
                  {[ 'class', 'yearGroup' ].includes(values.cohort?.name) ? (
                    <div className={'w-2/3'}>
                      <Selector
                        id={'cohort.id'}
                        label={''}
                        placeholder={'Select...'}
                        hideLabel
                        isClearable={false}
                        options={cohortOptions}
                        onChange={(options: OptionType<string>[]) => setFieldValue('cohort.id', options.map((option) => option.value))}
                        value={
                          Array.isArray(values.cohort.id)
                            ? cohortOptions?.filter((option: OptionType<number>) => values.cohort.id?.includes(option.value)) || []
                            : []
                        }
                        error={errors.cohort?.id}
                        isMulti
                        isDisabled={cohortOptions === null}
                      />
                    </div>) : null }
                </div>
              </div>
              <div className={'space-y-2'}>
                <h3 className={'font-bold'}>Impact Metric</h3>
                <FieldArray name={'metrics'}>
                  {({ push, replace, remove }) => {
                    return (
                      <div className={'space-y-4'}>
                        {values.metrics.map((component, index) => (
                          <div
                            key={index}
                            className={'mt-2 flex items-center space-x-4'}
                          >
                            <div>
                              <span>
                                {t('pages.ADMIN.FORM.LABELS.METRIC_COMPONENT', { number: index + 1 })}
                              </span>
                            </div>
                            <div className={'flex-grow'}>
                              <Selector
                                id={`metrics[${index}]`}
                                label={t('pages.ADMIN.FORM.LABELS.METRIC_COMPONENT', { number: index + 1 })}
                                hideLabel
                                isClearable={false}
                                options={METRICS_COMPONENTS
                                  .filter((metricsComponent) => !values.metrics.some((option) => metricsComponent === option))
                                  .map((metricsComponent) => ({
                                    label: t(`pages.ADMIN.IMPACT_REPORTS.METRICS.${metricsComponent}`),
                                    value: metricsComponent,
                                  }))
                                }
                                onChange={(option: OptionType<number>) => replace(index, option.value)}
                                value={values.metrics[index]
                                  ? {
                                    label: t(`pages.ADMIN.IMPACT_REPORTS.METRICS.${values.metrics[index]}`),
                                    value: values.metrics[index],
                                  }
                                  : null}
                                error={errors.metrics?.[index]}
                              />
                            </div>
                            <ButtonIcon
                              type={'button'}
                              icon={GLOBAL_ICONS.delete}
                              size={'md'}
                              title={t('pages.ADMIN.FORM.LABELS.DELETE_METRIC_COMPONENT', { number: index + 1 })}
                              onClick={() => remove(index)}
                              asLink
                            />
                          </div>
                        ))}

                        <Button
                          type={'button'}
                          className={'w-full'}
                          isOutline

                          onClick={() => push(null)}
                          disabled={values.metrics.length >= METRICS_COMPONENTS.length}
                        >
                          {t('pages.ADMIN.FORM.LABELS.ADD_METRIC_COMPONENT')}
                        </Button>
                      </div>
                    );
                  }}
                </FieldArray>
                {(!isValid && Object.keys(errors).some((key) => touched[key as keyof GoalProps]))
                && (<FormGenericError />)}
              </div>
              <div className={'space-y-2'}>
                <h3 className={'font-bold'}>Notes</h3>
                <Textarea
                  id={'notes'}
                  label={''}
                  hideLabel
                  placeholder={'Notes...'}
                  onChange={(event) => setFieldValue('notes', event.target.value)}
                  value={values.notes ?? ''}
                  error={errors.notes}
                />
              </div>
            </div>
            <ModalFooter justify={editMode ? 'between' : 'end'}>
              {editMode && (
                <CancelButtonModal
                  align={'start'}
                  onClick={() => resetForm()}
                >
                  {t('pages.ROSTER_MANAGEMENT.CLASSES.ADD_EDIT.BUTTONS.DISCARD')}
                </CancelButtonModal>
              )}
              <SaveButton type={'submit'} />
            </ModalFooter>
            {editMode && (
              <div className={'container space-y-6'}>
                <h5 className={'font-bold'}>
                  {'Delete Goal'}
                </h5>
                <Button
                  color={'negative'}
                  icon={GLOBAL_ICONS.delete}
                  isOutline
                  onClick={handleDelete}
                  disabled={!editMode}
                >
                  {'Delete Goal'}
                </Button>
              </div>
            )}
          </form>
        );}}
    </Formik>
  );
};
interface GoalModalProps extends Pick<ToggleModalProps, 'toggle'> {
  toggleConfirm(isOpen: boolean): void;
  data?: GoalProps;
  defaultGoals: DefaultGoals[];
  yearGroups: NewClassGetYearGroupsItem[];
  schoolClasses: NewClassGetItem[];
  onSuccess?: (formData?: GoalProps) => void;
  onError?: () => void;
}
interface GoalProps {
  goal_key: string | null;
  cohort: {
    id: number[] | null;
    name: string | null;
  }
  metrics: (string | null)[];
  notes: string | null;
}

type DefaultGoals = {
  goal_key: string;
  metrics: string[];
};

export type {
  GoalModalProps,
  GoalProps,
  DefaultGoals,
};

export { AddEditGoalModal };
